import React, {useEffect, useRef, useState} from 'react';
import './MatchWinnerModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {getFactionConfig} from '../../../utils/factions/getFactionConfig';
import {getFactionSkinDisplayProps} from '../../../utils/factions/getFactionSkinDisplayProps';
import {getPlayerHexColor} from '../../../utils/players/getPlayerHexColor';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {pickMatchWinnerCongratulation} from '../../../configs/matchWinnerCongratulations';
import {getPlaceIconPath} from '../../../utils/gameEnd/getPlaceIconPath';
import {getLeagueByRating} from '../../../utils/rating/getLeagueByRating';
import {MINUS_SIGN} from '../../../constants/text';
import {useBlurCoveredTextEntry} from '../../../hooks/input/useBlurCoveredTextEntry';
import AiPlayerMark from '../../VisibleBotMark/AiPlayerMark';

const PAGE_ID = 'MatchWinnerModal';

// Пауза между фанфарами появления и голосом фракции победителя
export const FACTION_SOUND_DELAY_MS = 2500;

// Фанфары начала игры. В матче — звуком сцены (playGameStart); вне матча
// (превью в админке) у меню этот метод пустой, и тот же звук играет
// playRankLobbyStart
const playWinnerFanfare = () => {
  const gameSoundManager = REGISTRY_SINGLETON.getGameScene()?.soundManager;
  if (gameSoundManager?.playGameStart) {
    gameSoundManager.playGameStart();
    return;
  }
  MENU_SOUND_MANAGER?.playRankLobbyStart?.();
};

/**
 * Торжество победителя в конце матча — поверх итогов, в стиле наградных
 * модалок (открытие фракции, скина): образ фракции победителя, подсветка и
 * акценты цветом игрока, случайное поздравление и случайная кнопка.
 *
 * Показывается всем участникам и зрителям: победителю — его триумф, остальным —
 * то, ради чего хочется выиграть следующий матч.
 *
 * Под поздравлением — итоговые очки, а в рейтинговой партии вместо них
 * изменение рейтинга и новый рейтинг с лигой, как в плашке игрока в итогах
 * матча (GameEndPlayerInfo). Явному боту рейтинг не меняют — у него очки.
 * ИИ-игрок промо-матча — полноценный игрок: рейтинг как у людей, плюс метка ИИ
 * у никнейма.
 *
 * @param {{name: string, color: string, factionId: string, selectedSkin?: string, isVisibleBot?: boolean, isAi?: boolean}} player - занявший первое место
 * @param {number} [finalWinPoints] - итоговые очки победителя
 * @param {boolean} [isRanked] - рейтинговая партия
 * @param {number} [ratingDelta] - изменение рейтинга победителя
 * @param {number} [rating] - новый рейтинг победителя
 */
function MatchWinnerModal({player, finalWinPoints, isRanked = false, ratingDelta, rating, onClose}) {
  const [visible, setVisible] = useState(false);
  // Поздравление выбирается один раз на показ — не меняется при перерисовках
  const [congratulation] = useState(() => pickMatchWinnerCongratulation());
  const closingRef = useRef(false);

  const factionConfig = getFactionConfig(player?.factionId);
  const {imagePath} = getFactionSkinDisplayProps(factionConfig, player?.selectedSkin);
  const playerColor = getPlayerHexColor(player?.color);

  useEffect(() => {
    // Появление — фанфары начала игры, а через паузу — голос фракции
    // победителя (без вступительного аккорда). Закрыли раньше — голоса не будет
    playWinnerFanfare();
    const factionSoundTimer = setTimeout(() => {
      if (player?.factionId && MENU_SOUND_MANAGER?.playFactionSelect) {
        MENU_SOUND_MANAGER.playFactionSelect(player.factionId, true);
      }
    }, FACTION_SOUND_DELAY_MS);
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => {
      clearTimeout(factionSoundTimer);
      clearTimeout(showTimer);
    };
  }, [player?.factionId]);

  const handleClose = () => {
    if (closingRef.current) {
      return;
    }
    closingRef.current = true;
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  };
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  // Enter и пробел — «поздравить», то есть закрыть эту модалку. Перехватываем
  // до итогов матча под ней: они закрываются теми же клавишами
  // (useCloseOnEnterSpace на window) и спрятали бы итоги вместе с торжеством.
  // Фокус мог остаться в поле чата матча под модалкой — забираем его, чтобы
  // эти клавиши поздравляли, а не дописывали спрятанное сообщение
  useBlurCoveredTextEntry();
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        handleCloseRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  if (!player || !factionConfig) {
    return null;
  }

  // В рейтинговой партии рейтинг приходит с сервера чуть позже итогов: пока
  // его нет, строка пустая — чтобы очки не мелькнули и не сменились рейтингом
  const isRatingShown = isRanked && !player.isVisibleBot;
  const hasRatingInfo = typeof ratingDelta === 'number' && typeof rating === 'number';

  const renderResult = () => {
    if (isRatingShown) {
      if (!hasRatingInfo) {
        return null;
      }
      const league = getLeagueByRating(rating);
      return (
        <div className="match-winner-rating text-rating">
          <span>Рейтинг:</span>
          <span className="match-winner-rating-delta">
            {ratingDelta >= 0 ? `+${ratingDelta}` : `${MINUS_SIGN}${Math.abs(ratingDelta)}`}
          </span>
          <span className="match-winner-rating-arrow" aria-hidden="true"/>
          <span className="match-winner-rating-value">{rating}</span>
          <img
            className="match-winner-league-icon"
            src={league.iconPath}
            alt={league.name}
            title={league.name}
          />
        </div>
      );
    }
    if (typeof finalWinPoints !== 'number') {
      return null;
    }
    return (
      <div className="match-winner-points">
        <img className="match-winner-points-icon" src="/img/icons/win points.png" alt="Очки"/>
        <span>{finalWinPoints}</span>
      </div>
    );
  };

  return (
    <div
      className={`match-winner-overlay ${visible ? 'match-winner-overlay_visible' : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`match-winner-modal ${visible ? 'match-winner-modal_visible' : ''}`}
        style={{'--player-color': playerColor}}
      >
        <div className="match-winner-content">
          <div className="match-winner-place">
            <img
              className="match-winner-place-icon"
              src={getPlaceIconPath(0)}
              alt={''}
              aria-hidden={'true'}
            />
            <span>Первое место</span>
          </div>

          <div className="match-winner-image-wrapper">
            <img
              className="match-winner-image"
              src={imagePath}
              alt={factionConfig.name}
            />
          </div>

          <div className="match-winner-info">
            {/* Поздравление — главный заголовок, никнейм в нем выделен цветом
                игрока: отдельной строкой ник повторялся бы дважды подряд */}
            <h2 className="match-winner-congratulation">
              {congratulation.before}
              <span className="match-winner-name">
                {player.name}
                {/* Победил ИИ-игрок промо-матча — метка ИИ прямо у никнейма */}
                {player.isAi && <AiPlayerMark/>}
              </span>
              {congratulation.after}
            </h2>
            {renderResult()}
          </div>

          <div className="main-menu-buttons match-winner-buttons">
            <PageButton onClick={handleClose} pageId={PAGE_ID} buttonId="congratulate">
              {congratulation.buttonText}
            </PageButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MatchWinnerModal;
